/**
 * region_home1 (2026-10-18 · instr/cl/region_home1 · region-a) — 홈 «지역 범위» 순수 규칙(서버·클라이언트 공용 · 테스트 대상 · DB 0).
 *
 *   · 히어로 카드(HeroCard) 꼴 · 정렬(일정 임박순 = imagegen/schedule-priority compareScheduleKey · 이미지 있는 현장 먼저) · 폴백 사슬
 *     (지역 현장 → 「{지역} 주요 현장」 정적 카드 → 전국 상위 + 「전국」 칩).
 *   · 머리줄 「부산 · 10월 18일 기준」 · 섹션 공용 지역 필터(시도 = region 열 · 시군 = sigungu 접두 일치).
 *   ⛔ 지역 이름·개수 하드코딩 없음 — 이름은 언제나 호출부(regions 표)에서 온다.
 */
import { kstYmd } from '@/lib/cl-core/kst';
import type { HeroFeature, HeroStat } from '@/components/ds2/Hero';
import { countRceptInWindow, type ScheduleEvent } from '@/lib/rx/schedule-events';
import { compareScheduleKey, type ScheduleKey } from '@/lib/imagegen/schedule-priority';

export type ScopeChip = '전국' | null;

/** 히어로 카드 1장 — ds2 HeroFeature 와 같은 정보 + 캐러셀용 필드. title 은 언제나 있다(빈 카드 0). */
export interface HeroCard {
  /** 'site:{slug}' | 'static:{code}' */
  key: string;
  slug: string | null;
  title: string;
  href: string;
  imageSrc: string | null;
  imageAlt: string | null;
  aiLabel: 'AI 상상도' | null;
  badge: string | null;
  dday: number | null;
  date: string | null;
  meta: string[];
  sido: string | null;
  scopeChip: ScopeChip;
  isStatic: boolean;
}

export const NATIONAL_SCOPE_LABEL = '전국';

/** 「10월 18일」(형식이 아니면 ''). ds2-home mdKo 와 같은 규칙(순수 모듈이라 따로 둔다 — 서버 의존 import 0). */
function mdKo(ymd: string | null | undefined): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(ymd ?? ''));
  return m ? `${Number(m[2])}월 ${Number(m[3])}일` : '';
}
function mdSlash(ymd: string | null | undefined): string {
  const m = /^(\d{4})-?(\d{2})-?(\d{2})/.exec(String(ymd ?? ''));
  return m ? `${Number(m[2])}/${Number(m[3])}` : '';
}

/** 머리줄 — 「부산 · 10월 18일 기준」 / 「전국 · 10월 18일 기준」. 지역 미선택(null·빈 값) = 전국(기본 HTML 바이트 그대로). */
export function homeScopeLabel(sido: string | null | undefined, todayKST: string): string {
  const s = String(sido ?? '').trim() || NATIONAL_SCOPE_LABEL;
  return `${s} · ${mdKo(todayKST)} 기준`;
}

/** HeroCard → ds2 Hero feature props. 전국 폴백 카드면 「전국」 범위 칩(region_home1b FeatureCard data-ds2-scope)으로 범위를 숨기지 않는다.
 *  scopeChip 이 없으면 키 자체를 넣지 않는다(종전 feature 객체와 같은 꼴 · 기본 HTML 불변). */
export function heroFeatureOf(c: HeroCard): HeroFeature {
  return {
    title: c.title,
    href: c.href,
    imageSrc: c.imageSrc,
    imageAlt: c.imageAlt,
    aiLabel: c.imageSrc ? c.aiLabel : null,
    badge: c.badge,
    meta: c.meta,
    ...(c.scopeChip ? { scopeChip: c.scopeChip } : {}),
  };
}

const norm = (v: string | null | undefined) => String(v ?? '').replace(/\s+/g, '');

/** 시도 열 값이 범위 안인가(「부산광역시」 ⊃ 「부산」). */
export function inSido(sidoNames: readonly string[], value: string | null | undefined): boolean {
  const v = norm(value);
  if (!v || !sidoNames.length) return false;
  return sidoNames.some((n) => { const k = norm(n); return !!k && (v === k || v.startsWith(k)); });
}
/** 시군구 값이 시·군 범위 안인가(sigun null = 시·도 전체 = 참 · 「창원시 의창구」 ⊂ 「창원시」). */
export function inSigunNames(sigunNames: readonly string[] | null | undefined, value: string | null | undefined): boolean {
  if (!sigunNames) return true;
  const v = norm(value);
  if (!v) return false;
  return sigunNames.some((n) => { const k = norm(n); return !!k && (v === k || v.startsWith(k)); });
}

export interface ScopeNames { sidoNames: readonly string[]; sigunNames: readonly string[] | null }

/** 섹션 공용 지역 필터(현장성 섹션 전부 같은 규칙). scope=null(전국) 이면 입력 그대로. */
export function filterByScope<T>(
  rows: readonly T[],
  scope: ScopeNames | null,
  sidoOf: (t: T) => string | null | undefined,
  sigunguOf?: (t: T) => string | null | undefined,
): T[] {
  if (!scope) return [...rows];
  return rows.filter((r) => inSido(scope.sidoNames, sidoOf(r)) && inSigunNames(scope.sigunNames, sigunguOf ? sigunguOf(r) : null));
}

/* ─── 히어로 후보 조립 ───────────────────────────────────────────────────── */

/** 히어로 후보 재료 1행(서버 로더가 채운다). */
export interface HeroSiteFact {
  slug: string;
  siteId: string;
  name: string;
  region: string | null;
  sigungu: string | null;
  units: number | null;
  /** 분양예정 라벨(「2026년 11월」) · 지난 예정이면 null. */
  periodLabel: string | null;
  /** lifecycle 라벨(「관리처분」 등) — 날짜 없는 일정의 태그. */
  stageLabel: string | null;
  image: { src: string; alt: string; ai: boolean } | null;
}

const dd = (n: number) => (n <= 0 ? 'D-day' : `D-${n}`);

/** 정렬 키 → 흰 태그 한 줄. 날짜 있는 일정 = 「청약 D-3 · 10/19 접수」 꼴. */
export function heroBadgeOf(k: ScheduleKey | null | undefined, f: Pick<HeroSiteFact, 'periodLabel' | 'stageLabel'>): string | null {
  if (!k) return f.stageLabel;
  if (k.dated && k.date) {
    const md = mdSlash(k.date);
    const d = dd(k.days ?? 0);
    if (k.kind === 'subscription') return `청약 ${d}${md ? ` · ${md} 접수` : ''}`;
    if (k.kind === 'model_house') return `모델하우스 ${d}${md ? ` · ${md}` : ''}`;
    if (k.kind === 'winner_contract') return `당첨·계약 ${d}${md ? ` · ${md}` : ''}`;
    if (k.kind === 'expected_sale') return f.periodLabel ? `분양예정 · ${f.periodLabel}` : '분양예정';
  }
  if (k.kind === 'expected_sale') return '분양예정';
  if (k.kind === 'winner_contract') return '당첨·계약';
  if (k.kind === 'unsold') return '미분양·선착순';
  return f.stageLabel;
}

/** 재료 1행 + 키 → 카드. */
export function heroCardOf(f: HeroSiteFact, k: ScheduleKey | null | undefined, scopeChip: ScopeChip = null): HeroCard {
  return {
    key: `site:${f.slug}`,
    slug: f.slug,
    title: f.name,
    href: `/apt/${encodeURIComponent(f.slug)}`,
    imageSrc: f.image?.src ?? null,
    imageAlt: f.image?.alt ?? null,
    aiLabel: f.image?.ai ? 'AI 상상도' : null,
    badge: heroBadgeOf(k, f),
    dday: k?.dated ? k.days ?? null : null,
    date: k?.dated ? k.date : null,
    meta: [[f.region, f.sigungu].filter(Boolean).join(' '), f.units ? `${f.units.toLocaleString('ko-KR')}세대` : null].filter((x): x is string => !!x),
    sido: f.region,
    scopeChip,
    isStatic: false,
  };
}

/**
 * 일정 임박순(compareScheduleKey) → 이미지 있는 현장 먼저(그 안에서도 임박순) → 이미지 없는 현장(임박순) · 상위 limit.
 *   키가 없는 행은 맨 뒤(입력 순서). slug 중복 제거.
 */
export function orderHeroFacts(facts: readonly HeroSiteFact[], keys: ReadonlyMap<string, ScheduleKey>, limit = 5): HeroSiteFact[] {
  const seen = new Set<string>();
  const uniq = facts.filter((f) => (f.slug && f.name && !seen.has(f.slug) ? (seen.add(f.slug), true) : false));
  const ranked = uniq
    .map((f, i) => ({ f, i, k: keys.get(f.siteId) }))
    .sort((a, b) => {
      const ia = a.f.image ? 0 : 1; const ib = b.f.image ? 0 : 1;
      if (ia !== ib) return ia - ib;
      if (a.k && b.k) return compareScheduleKey(a.k, b.k) || a.i - b.i;
      if (a.k || b.k) return a.k ? -1 : 1;
      return a.i - b.i;
    });
  return ranked.slice(0, Math.max(0, limit)).map((x) => x.f);
}

/** 「부산 주요 현장」 정적 카드 — 지역 안에 일정 현장이 하나도 없을 때(이미지 없는 텍스트 카드 · 허브 링크). */
export function staticHeroCard(opts: { code: string; label: string; hubHref: string; siteCount?: number | null }): HeroCard | null {
  const label = String(opts.label ?? '').trim();
  const href = String(opts.hubHref ?? '').trim();
  if (!label || !href) return null;
  const n = typeof opts.siteCount === 'number' && opts.siteCount > 0 ? opts.siteCount : null;
  return {
    key: `static:${opts.code}`, slug: null, title: `${label} 주요 현장`, href,
    imageSrc: null, imageAlt: null, aiLabel: null,
    badge: `${label} 전체 보기`, dday: null, date: null,
    meta: n ? [`현장 ${n.toLocaleString('ko-KR')}곳`] : [],
    sido: label, scopeChip: null, isStatic: true,
  };
}

/**
 * 폴백 사슬(규칙 2): 지역 카드가 있으면 그대로 → 없으면 정적 카드 1장 → 그것도 없으면 전국 카드(scopeChip '전국').
 */
export function heroFallbackChain(regional: readonly HeroCard[], staticCard: HeroCard | null, national: readonly HeroCard[], limit = 5): HeroCard[] {
  if (regional.length) return regional.slice(0, limit);
  if (staticCard) return [staticCard];
  return national.slice(0, limit).map((c) => ({ ...c, scopeChip: '전국' as const }));
}

/** 전국 기본 HTML 불변 — [0] = 종전 대표 현장(legacy) · 나머지는 prio 순(같은 현장 중복 제거). */
export function withLegacyFirst(legacy: HeroCard | null, ranked: readonly HeroCard[], limit = 5): HeroCard[] {
  if (!legacy) return ranked.slice(0, limit);
  return [legacy, ...ranked.filter((c) => c.slug !== legacy.slug)].slice(0, limit);
}

/** 종전 대표 현장(ds2-home HeroPick: pickHeroFromWeek · heroFromUpcoming) → 카드. heroFeatureOf 결과가 종전 feature props 와 같다. */
export function heroCardFromPick(
  p: { slug: string; name: string; meta: string[]; badge: string; image: { src: string; alt: string; ai: boolean } } | null | undefined,
  extra: { dday?: number | null; date?: string | null; sido?: string | null } = {},
): HeroCard | null {
  if (!p || !p.slug || !p.name) return null;
  return {
    key: `site:${p.slug}`, slug: p.slug, title: p.name, href: `/apt/${encodeURIComponent(p.slug)}`,
    imageSrc: p.image.src, imageAlt: p.image.alt, aiLabel: p.image.ai ? 'AI 상상도' : null,
    badge: p.badge, dday: extra.dday ?? null, date: extra.date ?? null, meta: [...p.meta],
    sido: extra.sido ?? null, scopeChip: null, isStatic: false,
  };
}

/* ─── 통계 4칸(지역) — 순수 ─────────────────────────────────────────────── */

export interface RegionStatInput {
  /** 「부산」 · 「김해시」 — 첫 칸 라벨 「{label} 현장」. */
  label: string;
  tier: 1 | 2;
  scope: ScopeNames;
  /** site-count(siteCountOf) 값 — 모르면 null(칸 생략). */
  siteCount: number | null;
  /** 지역 분양예정 수(fetchUpcomingCountRegion · 전국 get_kd_metrics.upcoming 과 같은 정의). */
  upcomingCount: number | null;
  /** 일정 전체(loadScheduleItemsAll 7일) · 시군 판정용 slug → sigungu. */
  schedule: ReadonlyArray<Pick<ScheduleEvent, 'kind' | 'subId' | 'rceptBgn' | 'rceptEnd' | 'region'> & { slug?: string | null }> | null;
  sigunguBySlug?: Readonly<Record<string, string | null>>;
  today?: string;
}

/** 순수 — 지역 「이번 주 청약」 = 전국과 같은 정의(접수 기간이 오늘~6일 뒤와 겹치는 청약 수) · 지역 범위만. */
export function regionWeekCount(inp: Pick<RegionStatInput, 'scope' | 'schedule' | 'sigunguBySlug' | 'today'>): number | null {
  if (!inp.schedule) return null;
  const sig = inp.sigunguBySlug ?? {};
  const rows = inp.schedule.filter((r) => inSido(inp.scope.sidoNames, r.region)
    && (!inp.scope.sigunNames || (!!r.slug && inSigunNames(inp.scope.sigunNames, sig[r.slug] ?? null))));
  return countRceptInWindow(rows, inp.today ?? kstYmd(), 6);
}

/** 순수 조립 — 값 없는 칸은 칸째 생략(0·「—」 금지 · 0 은 0 으로 낸다). */
export function buildRegionStats(
  inp: Pick<RegionStatInput, 'label'>,
  v: { sites: number | null; week: number | null; upcoming: number | null; unsold: number | null },
  attrs: { upcoming?: Record<`data-${string}`, string> | null; unsold?: Record<`data-${string}`, string> | null } = {},
): HeroStat[] {
  const rows: Array<{ label: string; value: number | null; dataAttrs?: Record<`data-${string}`, string> | null }> = [
    { label: `${inp.label} 현장`, value: v.sites },
    { label: '이번 주 청약', value: v.week },
    { label: '분양예정', value: v.upcoming, dataAttrs: attrs.upcoming },
    { label: '미분양·선착순', value: v.unsold, dataAttrs: attrs.unsold },
  ];
  return rows.flatMap((s) => (typeof s.value === 'number' && Number.isFinite(s.value) && s.value >= 0
    ? [{ label: s.label, value: s.value, ...(s.dataAttrs ? { dataAttrs: s.dataAttrs } : {}) }]
    : []));
}

